import type { ReactNode } from "react";
import { Brand } from "./Brand";
import { cn } from "@/shared/utils/cn";

interface SetupHeaderProps {
  title: string;
  illustration?: string;
  step?: 1 | 2 | 3 | 4;
  stage?: string;
  centered?: boolean;
  hideTitle?: boolean;
  titleSize?: "default" | "welcome";
  children?: ReactNode;
}

export function SetupHeader({ title, illustration, step, stage, centered = false, hideTitle = false, titleSize = "default", children }: SetupHeaderProps) {
  return (
    <header className="mx-auto w-full max-w-[560px] shrink-0 px-4 pt-6 pb-5 sm:px-[30px]">
      <div className={cn("flex items-center justify-between gap-3", centered && "flex-col text-center")}>
        <div className={cn("grid gap-4", centered && "justify-items-center")}>
          <Brand wordmark />
          <h1 className={cn("text-xl font-extrabold", titleSize === "welcome" && "text-[40px] leading-tight", hideTitle && "sr-only")}>{title}</h1>
        </div>
        {illustration && <img src={illustration} alt="" className={cn("w-24 shrink-0 sm:w-[120px]", centered && "w-60 sm:w-60")} />}
      </div>
      {step && (
        <div role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={4} aria-valuenow={step} aria-valuetext={`${stage ?? title}, step ${step} of 4`} className="mt-5 grid gap-2.5">
          <div className="flex justify-between gap-3 text-xs">
            <span className="font-bold text-primary">{stage ?? title}</span>
            <span className="text-muted-foreground">Step {step} of 4</span>
          </div>
          <div className="flex gap-1.5" aria-hidden="true">
            {[1, 2, 3, 4].map((segment) => <span key={segment} className={cn("h-[5px] flex-1 rounded-full", segment <= step ? "bg-primary" : "bg-border")} />)}
          </div>
        </div>
      )}
      {children}
    </header>
  );
}
